import React, { useEffect, useMemo, useRef, useState } from 'react'
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd'
import { Box, Collapse, Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import MasonryTile from '../../../components/Masonry/MasonryTile'
import { ChevronLeft, ChevronRight, People } from '@mui/icons-material'
import { useGetDictionariesQuery } from '../../../store/redux/reducers/dictionarySlice'
import { GeneralCandidate } from '../../../store/types/models/GeneralCandidate'
import CandidateCard from './CandidateCard'
import CandidateKanbanFormDialog from '../../../components/Dialog/CandidateKanbanFormDialog'
import CommentsSidebar from '../../../components/Sidebar/comments/CommentsSidebar'
import { t } from 'i18next'
import DecisionEnum from '../../../store/types/enums/DecisionEnum'
import { useNotification } from '../../../store/redux/hooks'

export interface KanbanColumn {
  id: string
  title: string
  items: GeneralCandidate[]
  isCollapsed?: boolean
}

interface Columns {
  [key: string]: KanbanColumn
}

interface KanbanDemoProps {
  candidates?: GeneralCandidate[]
}

const CandidatesKanbanBoard = ({ candidates }: KanbanDemoProps) => {
  const { displayNotification } = useNotification()
  const { data: stagesResponse } = useGetDictionariesQuery({
    page: 0,
    size: 20,
    group: 'RECRUITMENT_WORKFLOW',
    sortField: 'order',
  })

  const archiveColumnId = useMemo(() => {
    const archive = stagesResponse?.content.find(stage => stage.key === 'RECRUITMENT_ARCHIVE')
    return archive?.id
  }, [stagesResponse?.content])

  const initialColumns: Columns = useMemo(() => {
    if (!stagesResponse?.content) return {}

    return stagesResponse.content.reduce((acc, stage) => {
      let items = candidates?.filter(value => value.recruitment.stage === stage.value) || []
      acc[stage.id] = {
        id: stage.id,
        title: stage.value,
        items: items,
        isCollapsed: items.length === 0,
      }
      return acc
    }, {} as Columns)
  }, [stagesResponse, candidates])

  const [columns, setColumns] = useState<Columns>(initialColumns)

  useEffect(() => {
    setColumns(initialColumns)
  }, [initialColumns])

  const [openDialog, setOpenDialog] = useState<boolean>(false)
  const [movedCandidate, setMovedCandidate] = useState<GeneralCandidate>()
  const [destination, setDestination] = useState<KanbanColumn>()
  const [revertDrag, setRevertDrag] = useState<() => void>(() => () => {})

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result
    if (!destination) return

    const sourceCol = columns[source.droppableId]
    const destCol = columns[destination.droppableId]
    const [movedItem] = sourceCol.items.splice(source.index, 1)
    setDestination(destCol)
    setMovedCandidate(movedItem)

    if (source.droppableId === destination.droppableId) {
      sourceCol.items.splice(destination.index, 0, movedItem)
      setColumns({
        ...columns,
        [sourceCol.id]: { ...sourceCol },
      })
    } else {
      const revert = () => {
        sourceCol.items.splice(source.index, 0, movedItem)
        destCol.items.splice(destination.index, 1)
        setColumns({
          ...columns,
          [sourceCol.id]: { ...sourceCol },
          [destCol.id]: { ...destCol },
        })
      }
      if (movedItem.recruitment.decision === DecisionEnum.REJECTED && destCol.id !== archiveColumnId) {
        displayNotification({ message: t('page.candidates.rejectedCandidateMovedError'), type: 'error' })
        revert()
        return
      }
      setOpenDialog(true)
      destCol.items.splice(destination.index, 0, movedItem)
      destCol.isCollapsed = false
      setColumns({
        ...columns,
        [sourceCol.id]: { ...sourceCol },
        [destCol.id]: { ...destCol },
      })
      setRevertDrag(() => revert)
    }
  }

  const toggleColumnCollapse = (columnId: string) => {
    setColumns(prevColumns => ({
      ...prevColumns,
      [columnId]: {
        ...prevColumns[columnId],
        isCollapsed: !prevColumns[columnId].isCollapsed,
      },
    }))
  }

  const ref = useRef<HTMLDivElement>(null)

  const isAuto = useMemo(() => {
    const nonCollapsedColumns = Object.values(columns).filter(col => !col.isCollapsed)
    const maxItems = nonCollapsedColumns.reduce((max, col) => Math.max(max, col.items.length), 0)
    return maxItems > 3
  }, [columns])

  const [commentSidebarOpen, setCommentSidebarOpen] = useState(false)
  const [commentCandidate, setCommentCandidate] = useState<GeneralCandidate>()

  return (
    <>
      <DragDropContext onDragEnd={onDragEnd}>
        <MasonryTile sx={{ width: '100%', height: '70vh', minHeight: '650px', pb: isAuto ? 2 : 0, px: 2, pt: 2 }}>
          <Stack sx={{ overflow: 'auto', p: 1, m: -1, height: isAuto ? 'auto' : '100%' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 1.5,
                width: 'max-content',
                p: 2,
                pb: 0,
                height: isAuto ? 'auto' : '100%',
              }}
            >
              {Object.entries(columns).map(([columnId, column]) => (
                <React.Fragment key={columnId}>
                  <Box sx={{ display: 'flex', flexDirection: 'row', height: column.isCollapsed ? 'auto' : '100%' }}>
                    <Collapse in={!column.isCollapsed} orientation={'horizontal'}>
                      <Box
                        key={columnId}
                        sx={{
                          width: 300,
                          transition: 'width 300ms ease, padding 300ms ease',
                          height: '100%',
                        }}
                      >
                        <Box
                          ref={ref}
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            height: 'fit-content',
                            overflow: 'hidden',
                            pb: 2,
                          }}
                        >
                          <Typography variant="h5">{column.title}</Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <People sx={{ color: 'background.elevation9' }} />
                            {column.items.length}
                          </Box>
                        </Box>
                        {!column.isCollapsed && (
                          <Droppable droppableId={columnId}>
                            {(provided, snapshot) => {
                              return (
                                <Box
                                  ref={provided.innerRef}
                                  {...provided.droppableProps}
                                  sx={{
                                    borderRadius: 1,
                                    height: `calc(100% - ${ref?.current?.clientHeight}px)`,
                                    bgcolor: snapshot.isDraggingOver ? 'background.elevation2' : 'auto',
                                  }}
                                >
                                  {column.items.map((item, index) => (
                                    <Draggable draggableId={item.id} index={index} key={item.id}>
                                      {(provided, snapshot) => (
                                        <Box
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          {...provided.dragHandleProps}
                                          marginBottom={1.5}
                                        >
                                          <CandidateCard
                                            candidate={item}
                                            setCommentSidebarOpen={setCommentSidebarOpen}
                                            setCommentCandidate={setCommentCandidate}
                                            isDragged={snapshot.isDragging}
                                          />
                                        </Box>
                                      )}
                                    </Draggable>
                                  ))}
                                  {provided.placeholder}
                                </Box>
                              )
                            }}
                          </Droppable>
                        )}
                      </Box>
                    </Collapse>
                    <Collapse in={column.isCollapsed} orientation={'horizontal'}>
                      <Box
                        key={`${columnId}-collapsed`}
                        sx={{
                          width: 15,
                          height: '100%',
                          display: 'flex',
                          position: 'relative',
                        }}
                      >
                        {column.isCollapsed && (
                          <Droppable droppableId={columnId}>
                            {(provided, snapshot) => {
                              return (
                                <Box
                                  ref={provided.innerRef}
                                  {...provided.droppableProps}
                                  sx={{
                                    mt: 2,
                                    display: 'flex',
                                    bgcolor: snapshot.isDraggingOver ? 'background.elevation2' : 'auto',
                                    width: 15,
                                    overflow: 'hidden',
                                    ml: '2px',
                                  }}
                                >
                                  {provided.placeholder}
                                </Box>
                              )
                            }}
                          </Droppable>
                        )}

                        <Box
                          sx={{
                            position: 'absolute',
                            top: '32px',
                            left: '50%',
                            transform: 'translateX(16px) rotate(90deg)',
                            transformOrigin: 'top left',
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 1,
                            ml: '-2px',
                          }}
                        >
                          <Typography variant="h5" noWrap>
                            {column.title}
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <People sx={{ color: 'background.elevation9' }} />
                            {column.items.length}
                          </Box>
                        </Box>
                      </Box>
                    </Collapse>
                  </Box>
                  <Box sx={{ position: 'relative', display: 'flex', px: 2, flex: 1 }}>
                    <Divider orientation="vertical" flexItem sx={{ height: '100%' }} />
                    <Tooltip
                      title={column.isCollapsed ? t('page.candidates.expand') : t('page.candidates.collapse')}
                      placement={'top'}
                    >
                      <IconButton
                        onClick={() => toggleColumnCollapse(columnId)}
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          backgroundColor: 'background.elevation2',
                          boxShadow: 2,
                          borderRadius: '50%',
                          padding: 0.25,
                          zIndex: 2,
                          '&:hover': {
                            backgroundColor: 'background.elevation4',
                          },
                        }}
                      >
                        {!column.isCollapsed ? <ChevronLeft /> : <ChevronRight />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </React.Fragment>
              ))}
            </Box>
          </Stack>
        </MasonryTile>
      </DragDropContext>
      <CandidateKanbanFormDialog
        open={openDialog}
        stage={destination}
        candidateId={movedCandidate?.id}
        fullName={
          movedCandidate
            ? `${movedCandidate.firstName} ${movedCandidate.lastName}`
            : t('page.candidates.dialog.unknownCandidate')
        }
        onClose={() => {
          revertDrag()
          setOpenDialog(false)
        }}
        setOpenDialog={setOpenDialog}
        decision={movedCandidate?.recruitment.decision}
      />
      <CommentsSidebar
        open={commentSidebarOpen}
        setOpen={setCommentSidebarOpen}
        commentedCandidate={commentCandidate}
      />
    </>
  )
}

export default CandidatesKanbanBoard